import { useId, useState } from 'react';
export interface QuestionAnswer {
  choiceId?: string;
  answer: string;
}
export interface QuestionCardProps {
  question: {
    prompt: string;
    reason?: string;
    helperText?: string;
    choices?: Array<{ id: string; label: string; description?: string }>;
  };
  value: QuestionAnswer;
  onChange: (value: QuestionAnswer) => void;
  disabled?: boolean;
}
/** Choices are suggestions. A saved answer is the only reason to select one on load. */
export default function QuestionCard({
  question,
  value,
  onChange,
  disabled = false,
}: QuestionCardProps) {
  const id = useId();
  const [customSelected, setCustomSelected] = useState(false);
  const ownAnswerSelected = !value.choiceId && (customSelected || !!value.answer);
  return (
    <fieldset className="context-question-card" disabled={disabled}>
      <legend>
        <span role="heading" aria-level={3}>
          {question.prompt}
        </span>
      </legend>
      {(question.reason || question.helperText) && (
        <p className="context-help">{question.reason || question.helperText}</p>
      )}
      {!!question.choices?.length && (
        <div className="context-question-choices">
          {question.choices.map((choice) => (
            <label key={choice.id} className={value.choiceId === choice.id ? 'is-selected' : ''}>
              <input
                type="radio"
                name={id}
                value={choice.id}
                checked={value.choiceId === choice.id}
                onChange={() => {
                  setCustomSelected(false);
                  onChange({ choiceId: choice.id, answer: '' });
                }}
              />
              <span>
                <strong>{choice.label}</strong>
                {choice.description && <small>{choice.description}</small>}
              </span>
            </label>
          ))}
          <label className={ownAnswerSelected ? 'is-selected' : ''}>
            <input
              type="radio"
              name={id}
              checked={ownAnswerSelected}
              onChange={() => {
                setCustomSelected(true);
                onChange({ answer: value.choiceId ? '' : value.answer });
              }}
            />
            <span>
              <strong>Write my own answer</strong>
            </span>
          </label>
        </div>
      )}
      <label className="context-answer-label" htmlFor={`${id}-answer`}>
        {question.choices?.length ? (
          'Your own answer'
        ) : (
          <span className="sr-only">Your answer</span>
        )}
        <textarea
          id={`${id}-answer`}
          aria-label="Your answer"
          rows={3}
          maxLength={4000}
          value={value.choiceId ? '' : value.answer}
          onChange={(event) => {
            setCustomSelected(true);
            onChange({ answer: event.target.value });
          }}
          placeholder="Write in your own words. A few specific details are useful."
        />
      </label>
    </fieldset>
  );
}
